<script setup lang="ts">
import {
    faArrowLeft,
    faCardsBlank,
    faMouse,
} from '@fortawesome/pro-regular-svg-icons';
import {
    faBuilding,
    faPhoneAlt as falPhoneAlt,
    faUserTie,
} from '@fortawesome/pro-light-svg-icons';
import {
    faHeadphones,
    faTicket,
    faUser,
    faShoppingCart,
    faPhoneAlt as fadPhoneAlt,
    faReceipt,
    faShapes,
    faGlassWater,
    faCarBus,
    faPlane as fadPlane,
    faPlaneDeparture as fadPlaneDeparture,
    faPrint,
    faUserCheck,
    faUsers,
    faChartLine,
    faMobile,
    faBuildings,
} from '@fortawesome/pro-duotone-svg-icons';
import { faPhone } from '@fortawesome/pro-solid-svg-icons';
import {
    OrganizationDetailDocument,
    OrganizationStatisticsDocument,
} from '@schema/directus';
import Regex from '@static/regex';

const gtm = useGtm();
definePageMeta({
    layout: 'landing',
    whiteLabel: 'mrbilit',
});
const title = 'فروش سازمانی بلیط و خدمات سفر - مِستربلیط';
const desc =
    'ارائه تمامی خدمات مِستربلیط به سازمان‌ها، کسب و کارها و آژانس‌های مسافرتی';
useServerHead({
    link: [
        {
            rel: 'canonical',
            href: 'https://mrbilit.com/b2b',
        },
    ],
});
const callNumber = ref('');
const name = ref('');
const organizationName = ref('');
const description = ref('');
const form = new Form({
    callNumber: new Field(callNumber, {
        required: true,
        validator: value =>
            Regex.internationalPhoneNumber.full.test(
                convertPersianNumbers(value ?? '')
            ),
        partialValidator: value =>
            !value || Regex.internationalPhoneNumber.partial.test(value),
        maxLength: 16,
    }),
    name: new Field(name, {
        required: true,
        validator: value => Regex.name.full.test(value ?? ''),
    }),
    organizationName: new Field(organizationName, {
        required: true,
        validator: value => Regex.name.full.test(value ?? ''),
    }),
    description: new Field(description, {
        required: true,
    }),
});
const layoutStore = useLayoutStore();
const { organizationalSupportNumber } = classToRefs(layoutStore);
const formRef = ref<HTMLDivElement>();
const analyticsStore = useAnalyticsStore();
useSeoMeta({
    title,
    description: desc,
    ogTitle: title,
    ogDescription: desc,
});

const advantages = [
    {
        title: 'خرید مدت‌دار و اعتباری',
        icon: faShoppingCart,
        description:
            'خرید اعتباری و پس‌پرداخت با ارائه فاکتور رسمی و صورت وضعیت',
    },
    {
        title: 'پشتیبانی اختصاصی ۲۴ ساعته',
        icon: faHeadphones,
        description: 'پشتیبانی اختصاصی تلفنی و واتساپی، ۲۴ ساعته در طول هفته',
    },
    {
        title: 'خرید تلفنی و واتساپی',
        icon: fadPhoneAlt,
        description: 'خرید تلفنی نقدی و اعتباری به کمک پشتیبان اختصاصی',
    },
    {
        title: 'دریافت ظرفیت اختصاصی',
        icon: fadPlane,
        description:
            'تلاش می‌کنیم پرواز تکمیل ظرفیت‌شده را برایتان تهیه می‌کنیم.',
    },
    {
        title: 'فاکتور و صورت وضعیت رسمی',
        icon: faReceipt,
        description: 'فاکتور و صورت وضعیت خریدهایتان را آنلاین دریافت کنید.',
    },
    {
        title: 'خرید بیشتر، سود بیشتر',
        icon: fadPlaneDeparture,
        description: 'بازگشت بخشی از مبلغ خرید در قالب تخفیف و پورسانت',
    },
    {
        title: 'تمام خدمات سفر از یک درگاه',
        icon: faShapes,
        description:
            'مشاوره و رزرو انواع خدمات برای سفرهای کاری، رفاهی و گروهی',
    },
    {
        title: 'به راحتی آب خوردن',
        icon: faGlassWater,
        description:
            'خرید و کنسلی آنلاین، ثبت سفارش تلفنی، گزارش‌گیری یکپارچه و...',
    },
    {
        title: 'امکان خرید گروهی',
        icon: faCarBus,
        description: 'خرید یکجای چند خدمت، به راحتی و در کمترین زمان ممکن',
    },
];
const organizations = [
    'fadak',
    'system-group',
    'pars',
    'shatel',
    'kamvar',
    'majma',
    'mohaiman',
];
const features = [
    {
        icon: faUserCheck,
        title: 'تخصیص بودجه برای هر کاربری',
    },
    {
        icon: faUsers,
        title: 'ایجاد کاربر به تعداد نامحدود',
    },
    {
        icon: faChartLine,
        title: 'گزارش‌های مدیریتی خریدها و حسابداری',
    },
    {
        icon: faPrint,
        title: 'چاپ گروهی بلیط‌های خریداری شده',
    },
    {
        icon: faReceipt,
        title: 'قابلیت ارائه بلیط با لوگو و اطلاعات شخصی‌سازی‌شده',
    },
    {
        icon: faShoppingCart,
        title: 'داشتن پشتیبانی اختصاصی مالی و پشتیبان اختصاصی خرید بدون صف انتظار برای هر سازمان',
    },
];

const positions = [
    { id: 'مدیرعامل', label: 'مدیرعامل' },
    { id: 'هیئت مدیره', label: 'هیئت مدیره' },
    { id: 'منشی', label: 'منشی' },
    {
        id: 'مسئول خرید و بازرگانی',
        label: 'مسئول خرید و بازرگانی',
    },
    {
        id: 'مسئول رفاهیات و خرید بلیط',
        label: 'مسئول رفاهیات و خرید بلیط',
    },
    { id: 'کارشناس', label: 'کارشناس' },
    {
        id: 'خرید شخصی دارم و مسئول خرید شرکت نیستم',
        label: 'خرید شخصی دارم و مسئول خرید شرکت نیستم',
    },
];

const scrollToForm = () =>
    formRef.value?.scrollIntoView({ behavior: 'smooth' });
const { mutate } = useMutation(OrganizationDetailDocument);
const organizationStatistics = useQuery(OrganizationStatisticsDocument);
const addOrganization = async () => {
    if (!form.validateAll()) {
        nextTick(() => {
            navToSection();
        });
        return;
    }
    try {
        await mutate({
            OrganizationName: organizationName.value.trim(),
            CallNumber: callNumber.value.trim(),
            Name: name.value.trim(),
            Description: description.value.trim(),
        });
        gtm?.trackEvent({
            event: 'organization-request',
            organizationName: organizationName.value,
        });
        form.reset();
        swal({
            icon: 'success',
            text: 'درخواست شما با موفقیت ثبت شد.',
            confirmButtonText: 'باشه',
        });
    } catch (e) {
        await handleException({
            message:
                'درخواست شما ثبت شده است؛ همکاران ما با شما تماس خواهند گرفت.',
        });
        reportException(e);
    }
};
const statistics = computed(() => {
    const _organizationStatistics =
        organizationStatistics.result.value?.OrganizationStatistics;
    if (_organizationStatistics) {
        const {
            AppInstalls,
            FinalizedTickets,
            TrustedOrganizations,
            UniqueCustomers,
        } = _organizationStatistics;
        return [
            {
                icon: faUser,
                title: `${formatSeparator(Number(UniqueCustomers), ',')}+`,
                description: 'نفر مشتری یکتا تا امروز',
            },
            {
                icon: faMobile,
                title: `${formatSeparator(Number(AppInstalls), ',')}+`,
                description: 'نصب فعال اپلیکیشن',
            },
            {
                icon: faTicket,
                title: `${Number(FinalizedTickets) / 1000000}+`,
                description: 'میلیون بلیط صادر شده',
            },
            {
                icon: faBuildings,
                title: `${TrustedOrganizations}+`,
                description: 'سازمان به ما اعتماد کرده‌اند',
            },
        ];
    }
});
onMounted(() => {
    analyticsStore.trackAll('Visited_B2B_Landing');
});
</script>

<template>
    <div class="root-container">
        <div class="heading-container">
            <div class="content-container">
                <mr-typography
                    type="h2"
                    size="display1"
                    weight="700"
                    color="var(--cool-gray-100)"
                >
                    درخواست پنل سازمانی <br />
                    برای شرکت‌ها و سازمان
                </mr-typography>
                <mr-typography
                    size="x-large"
                    color="var(--cool-gray-300)"
                    :align="{
                        desktop: 'right',
                        mobile: 'center',
                    }"
                >
                    در صورتی که نماینده و یا مسئول فروش شرکت‌ها هستید، با
                    استفاده از راهکارهای سازمانی به مشتری خاص مِستربلیط تبدیل
                    شوید. ۲۴ ساعته در ۷ روز هفته از پشتیبانی اختصاصی سازمان‌ها
                    استفاده کنید و تلفنی یا واتساپی خرید خود را انجام دهید.
                    مشتریان سازمانی می‌توانند به صورت اعتباری و با قیمت
                    به‌صرفه‌تر خرید خود را انجام دهند.
                </mr-typography>
                <div class="actions-container">
                    <mr-button class="activate-btn" @click="scrollToForm">
                        فعالسازی پنل سازمانی
                    </mr-button>
                    <a
                        class="caller"
                        :href="`tel:+9821${organizationalSupportNumber}`"
                        target="_blank"
                    >
                        <span class="phone">
                            021-{{ organizationalSupportNumber }}
                        </span>
                        <mr-icon :icon="faPhone" color="var(--yellow-400)" />
                    </a>
                </div>
                <div class="hint">
                    <mr-icon
                        size="large"
                        :icon="faMouse"
                        color="var(--cool-gray-100)"
                    />
                    <mr-typography
                        size="x-large"
                        weight="700"
                        color="var(--cool-gray-100)"
                    >
                        برای اطلاعات بیشتر به پایین صفحه بروید
                    </mr-typography>
                </div>
            </div>
            <img class="bag-img" src="/img/b2b/b2b.png" alt="b2b" />
        </div>
        <mr-typography
            :size="{
                desktop: 'large',
                mobile: 'medium',
            }"
            color="var(--cool-gray-700)"
            class="header-subtitle"
        >
            مزایای سازمانی مِستربلیط
        </mr-typography>
        <mr-typography
            :size="{
                desktop: 'display3',
                mobile: 'display4',
            }"
            weight="700"
            color="var(--cool-gray-900)"
            class="header-title"
        >
            خدمات سازمانی، یک گام فراتر
        </mr-typography>
        <div class="advantages-container">
            <div
                v-for="advantage in advantages"
                :key="advantage.title"
                class="advantage"
            >
                <mr-icon
                    :icon="advantage.icon"
                    color="var(--blue-400)"
                    size="x-large"
                />
                <div class="body-container">
                    <mr-typography
                        :size="{
                            desktop: 'x-large',
                            mobile: 'medium',
                        }"
                        weight="700"
                        color="var(--cool-gray-900)"
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                        class="title"
                    >
                        {{ advantage.title }}
                    </mr-typography>
                    <mr-typography
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                        color="var(--cool-gray-800)"
                        class="description"
                    >
                        {{ advantage.description }}
                    </mr-typography>
                </div>
            </div>
            <div class="advantage full">
                <mr-icon
                    :icon="faCardsBlank"
                    color="var(--blue-400)"
                    size="x-large"
                />
                <div class="body-container">
                    <mr-typography
                        :size="{
                            desktop: 'x-large',
                            mobile: 'medium',
                        }"
                        weight="700"
                        color="var(--cool-gray-900)"
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                        class="title"
                    >
                        سفرکارت
                    </mr-typography>
                    <mr-typography
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                        color="var(--cool-gray-800)"
                        class="description"
                    >
                        اعتبار خرید سفر، به عنوان رفاهیات کارکنان، پاداش و
                        تشویقی و هدایای مناسبتی
                    </mr-typography>
                    <mr-button
                        link="nuxt"
                        href="/b2b/gift-card"
                        class="action-btn"
                    >
                        اطلاعات بیشتر
                    </mr-button>
                </div>
            </div>
        </div>
        <div class="organizations-container">
            <mr-typography
                :size="{
                    desktop: 'display3',
                    mobile: 'display4',
                }"
                weight="700"
                color="var(--cool-gray-900)"
                class="title"
            >
                با چه سازمان‌هایی همسفر شده‌ایم؟
            </mr-typography>
            <div class="organizations">
                <div
                    v-for="organization in organizations"
                    :key="organization"
                    class="organization"
                >
                    <img
                        :src="`/img/b2b/${organization}.png`"
                        :alt="organization"
                    />
                </div>
            </div>
        </div>
        <div class="features-container">
            <div class="features-background">
                <mr-typography
                    :size="{
                        desktop: 'display3',
                        mobile: 'display4',
                    }"
                    weight="700"
                    color="var(--cool-gray-900)"
                    class="title"
                >
                    امکانات پنل سازمانی مِستربلیط
                </mr-typography>
                <mr-typography
                    :size="{
                        desktop: 'large',
                        mobile: 'medium',
                    }"
                    color="var(--cool-gray-800)"
                    class="subtitle"
                >
                    همان ظاهر آشنا، با امکانات مورد نیاز شما
                </mr-typography>
                <div class="features">
                    <div
                        v-for="feature in features"
                        :key="feature.title"
                        class="feature"
                    >
                        <mr-icon :icon="feature.icon" color="var(--blue-400)" />
                        <mr-typography
                            :size="{
                                desktop: 'large',
                                mobile: 'medium',
                            }"
                            weight="500"
                            color="var(--cool-gray-900)"
                            :align="{
                                desktop: 'right',
                                mobile: 'center',
                            }"
                        >
                            {{ feature.title }}
                        </mr-typography>
                    </div>
                </div>
            </div>
        </div>
        <div ref="formRef" class="form-container">
            <div class="form-background">
                <form class="form" @submit.prevent.stop="addOrganization">
                    <mr-typography
                        :size="{
                            desktop: 'display3',
                            mobile: 'x-large',
                        }"
                        weight="700"
                        color="var(--blue-700)"
                        align="center"
                        class="title"
                    >
                        درخواست فعالسازی خدمات سازمانی
                    </mr-typography>
                    <mr-typography
                        size="small"
                        color="var(--blue-700)"
                        align="center"
                        class="form-alert"
                    >
                        این فرم ویژه‌ی درخواست شرکت‌ها و سازمان‌ها برای همکاری
                        با مِستربلیط است، در صورتی که نماینده‌ی فروش هستید، ثبت
                        درخواست کنید.
                    </mr-typography>
                    <mr-divider thickness="1px" color="var(--cool-gray-300)" />
                    <div class="form-fields">
                        <mr-input
                            v-bind="form.fields.name.attrs"
                            placeholder="نام و نام خانوادگی"
                        />
                        <mr-input
                            v-bind="form.fields.organizationName.attrs"
                            placeholder="نام سازمان"
                            :icon="faBuilding"
                        />
                        <mr-select
                            v-bind="form.fields.description.attrs"
                            placeholder="سمت شغلی شما"
                            :icon="faUserTie"
                            :options="positions"
                            full-width
                        />
                        <mr-input
                            v-bind="form.fields.callNumber.attrs"
                            placeholder="شماره موبایل"
                            :icon="falPhoneAlt"
                        />
                        <mr-button :left-icon="faArrowLeft" type="submit">
                            ثبت درخواست
                        </mr-button>
                    </div>
                </form>
            </div>
        </div>
        <div class="status-container">
            <div v-for="item in statistics" :key="item.title" class="status">
                <mr-icon
                    :icon="item.icon"
                    color="var(--blue-400)"
                    :size="{ desktop: 'x-large', mobile: 'medium' }"
                />
                <div class="body">
                    <mr-typography
                        :align="{ desktop: 'right', mobile: 'center' }"
                        color="var(--blue-200)"
                    >
                        <mr-typography
                            size="display3"
                            weight="700"
                            color="var(--blue-400)"
                            align="center"
                        >
                            {{ item.title }}
                        </mr-typography>
                        <mr-typography
                            :size="{
                                desktop: 'large',
                                mobile: 'medium',
                            }"
                            align="center"
                            weight="500"
                            color="var(--blue-700)"
                        >
                            {{ item.description }}
                        </mr-typography>
                    </mr-typography>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.root-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: white;

    .heading-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 40px;
        gap: 40px;
        border-radius: 16px;
        background: linear-gradient(97.64deg, #1c7cc9 11.39%, #031337 100%);
        max-width: 1200px;
        @include mobile-only(1160px) {
            flex-direction: column-reverse;
            background: linear-gradient(166.97deg, #1c7cc9 20.22%, #031337 100%)
                top center;
            height: auto;
            border-radius: 0;
            padding-inline: 0;
        }

        .content-container {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 40px;
            @include mobile-only(1160px) {
                align-items: center;
                padding: 0 30px 50px 30px;
            }
            .hint {
                display: flex;
                align-items: center;
                gap: 8px;
            }
            .actions-container {
                display: flex;
                align-items: center;
                gap: 60px;
                @include mobile-only(1160px) {
                    flex-direction: column;
                    max-width: 300px;
                    width: 100%;
                    gap: 20px;
                }

                .activate-btn {
                    width: 250px;
                    @include mobile-only(1160px) {
                        width: 100%;
                    }
                }

                .caller {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .phone {
                        @include tg-display4($w7, usePalette($p-yellow));
                    }
                }
            }
        }

        .bag-img {
            width: 100%;
            height: auto;
            max-width: 400px;
        }
    }

    .header-subtitle {
        margin-top: 40px;
        @include mobile-only {
            margin-top: 30px;
        }
    }

    .header-title {
        margin-top: 10px;
    }

    .advantages-container {
        margin-top: 50px;
        padding-inline: 5px;
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        width: 100%;
        max-width: 1200px;
        @include mobile-only {
            margin-top: 30px;
            padding-inline: 20px;
        }
        .advantage {
            display: flex;
            flex-basis: calc(33.33% - 10px);

            &.full {
                flex-basis: 100%;
            }
            border-radius: 10px;
            padding: 40px 30px;
            gap: 20px;
            border: 1px solid usePalette($p-cool-gray, $p-300);
            align-items: center;
            @include mobile-only {
                flex-direction: column;
                flex-basis: 100%;
                padding: 15px 30px;
                gap: 0;
            }

            .body-container {
                display: flex;
                flex-direction: column;
                gap: 10px;
                width: 100%;
                @include mobile-only {
                    gap: 0;
                }
                .action-btn {
                    width: fit-content;
                    margin-right: auto;
                    @include mobile-only {
                        width: 100%;
                    }
                }
            }
        }
    }

    .organizations-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: 40px;
        padding-block: 50px;
        width: 100%;
        max-width: 1200px;
        @include mobile-only {
            margin-top: 30px;
            padding: 20px;
        }

        .title {
            margin-bottom: 50px;
            @include mobile-only {
                margin-bottom: 30px;
            }
        }

        .organizations {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            width: 100%;
            max-width: 100%;
            overflow-y: auto;
            @include no-scrollbar;

            .organization {
                display: flex;
                flex-direction: column;
                align-items: center;
                flex-shrink: 0;
                min-width: 104px;
                @include mobile-only {
                    min-width: 100px;
                }

                img {
                    height: 70px;
                    @include mobile-only {
                        height: 60px;
                    }
                }
            }
        }
    }

    .features-container {
        width: 100%;
        max-width: 1200px;
        margin-bottom: 90px;
        @include mobile-only {
            margin-bottom: 50px;
        }

        .features-background {
            display: flex;
            flex-direction: column;
            align-items: center;
            border-radius: 30px;
            padding: 70px 130px;
            background: usePalette($p-yellow, $p-100);
            @include mobile-only {
                border-radius: 0;
                padding: 50px 20px;
            }

            .title {
                margin-bottom: 10px;
            }

            .subtitle {
                margin-bottom: 50px;
                @include mobile-only {
                    margin-bottom: 40px;
                }
            }

            .features {
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                gap: 30px 20px;
                @include mobile-only {
                    gap: 30px 10px;
                }

                .feature {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-basis: calc(33% - 13.33px);
                    @include mobile-only {
                        flex-direction: column;
                        flex-basis: calc(50% - 5px);
                    }
                }
            }
        }
    }

    .form-container {
        width: 100%;
        max-width: 1200px;
        margin-bottom: 40px;
        @include mobile-only {
            margin-bottom: 0;
        }

        .form-background {
            display: flex;
            flex-direction: column;
            align-items: center;
            background-position: center;
            background-image: linear-gradient(0deg, #00000080, #00000080),
                url('/img/b2b/hotelbg.jpeg');
            background-size: cover;
            border-radius: 30px;
            padding: 40px 100px;
            width: 100%;
            @include mobile-only {
                border-radius: 0;
                padding: 30px 20px;
            }

            .form {
                width: 450px;
                border-radius: 20px;
                background-color: white;
                @include mobile-only {
                    width: 100%;
                }

                .title {
                    padding: 30px 40px;
                    @include mobile-only {
                        padding: 20px 40px;
                    }
                }
                .form-alert {
                    padding-inline: 32px;
                }
                .form-fields {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 32px;
                    gap: 20px;
                    @include mobile-only {
                        padding: 20px;
                    }
                }
            }
        }
    }

    .status-container {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding-block: 20px;
        max-width: 1200px;
        gap: 30px;
        @include mobile-only {
            column-gap: 10px;
        }

        .status {
            display: flex;
            align-items: center;
            flex-basis: calc(25% - 22.5px);
            gap: 20px;
            @include mobile-only {
                flex-direction: column;
                flex-basis: calc(50% - 5px);
                gap: 10px;
            }

            .body {
                display: flex;
                flex-direction: column;
                @include mobile-only {
                    align-items: center;
                }
            }
        }
    }
}
</style>
